import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";

const BRAND = "#0f3c3f";
const ACCENT = "#10b981";
const TEXT = "#1f2937";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    lead:
      "A 60-second tour of how Vendorleads is organised so you can find what you need fast.",
    body: [
      {
        heading: "How the app is laid out",
        bullets: [
          "The left sidebar groups every feature under four headings: top-level (Dashboard, Contacts), AI Agent, Leads, and Tools.",
          "Account links — Settings, Billing, Upload Contacts, Send Feedback — sit at the bottom of the sidebar.",
          "If you're on a team, a 'Viewing' switcher lets you see your own leads, the whole team, or filter to one teammate.",
          "On mobile the sidebar collapses behind a hamburger menu; tap it to navigate between tabs.",
        ],
      },
      {
        heading: "Recommended first steps",
        bullets: [
          "Complete the onboarding wizard — it captures your branding so every report and email looks like yours from day one.",
          "Upload your contacts (CSV, .mbox export, or CRM connection) so the AI has something to work with.",
          "Open Settings and verify your email, agency name, mobile, and bio so they appear correctly on your landing page and outbound messages.",
          "Drop into Agent Chat and try a prompt like 'How many contacts do I have?' to see your AI co-pilot in action.",
        ],
      },
    ],
    tips: [
      "Most pages have a 'To Review' or similar filter — start your day there to action overnight engagement.",
      "Wherever you see the AI/Me toggle, you're deciding whether the AI handles that contact's replies and follow-ups or whether you stay in control.",
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    lead: "Real-time overview of your contact engagement and campaign performance.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Key metric cards: total contacts, emails delivered, opened, and clicked over the selected time period.",
          "Daily performance charts for Market Updates and Property Updates so you can spot delivery, open and click trends.",
          "Time period selector for 7, 30, 60 or 90-day windows.",
          "Last-updated timestamp so you know how fresh the stats are.",
          "Quick contact upload button to keep your database growing.",
        ],
      },
    ],
    tips: [
      "Use the 30-day view to spot trends — if opens dip, refresh your email copy or send time.",
      "Compare Property Update vs. Market Update clicks to learn which topics resonate with your audience.",
    ],
  },
  {
    id: "contacts",
    title: "Contacts",
    lead:
      "Manage your entire contact database with search, tags, ownership controls, and notification preferences.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "A list of every contact with name, email, phone and source badge (CRM, CSV, Domain, PropertyMe, etc.).",
          "Search by name or email, and filter by source.",
          "Notification badges telling you whether each contact gets Property Updates and Market Updates.",
          "An AI/Me ownership toggle per row — AI ownership lets the assistant reply and run follow-ups; Me keeps you in the driver's seat.",
          "Tags and AI handover notes you can attach so the AI personalises every interaction.",
          "Batch actions to retag, switch ownership, or adjust notifications for many contacts at once.",
          "An Upload Contacts modal supporting CSV, mailbox (.mbox) and CRM connection.",
        ],
      },
    ],
    tips: [
      "Tag hot prospects (e.g. 'Hot Lead — buying in 30 days') so the AI prioritises and personalises their messages.",
      "Leave a one-sentence handover note like 'Wants 4-bed in Bondi, budget $2.5M' — the AI will reference it on every reply.",
    ],
  },
  {
    id: "agent-chat",
    title: "Agent Chat",
    lead:
      "Conversational co-pilot that can research your database, draft articles, build appraisals and more.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "A chat panel with suggested prompts to get you started (e.g. 'How many contacts do I have?', 'Write an article about Surry Hills').",
          "Live thinking indicators showing what the AI is doing — looking up data, drafting, designing.",
          "Generated assets (articles, properties, appraisals) appearing inline as clickable cards.",
          "One-tap deep links into the asset's full editor so you can refine before publishing or sending.",
        ],
      },
    ],
    tips: [
      "Chain steps in a single prompt — e.g. 'Find Bondi contacts who opened my last 3 emails and write them an article on rate cuts'.",
      "Re-use the same prompt weekly; the AI will pull fresh data each time, giving you recurring content without extra effort.",
    ],
  },
  {
    id: "ai-conversations",
    title: "AI Conversations",
    lead:
      "Live view of every conversation the AI is having (or could be having) with your contacts.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Conversation list with contact name, email, phone and tags.",
          "Lead score badges colour-coded by engagement so high-intent contacts surface first.",
          "AI/Me ownership toggle per conversation — flip an active thread back to yourself any time.",
          "Search and tag filters to slice by audience (e.g. 'Investor', 'Bondi').",
          "Full message history and handover notes when you open a contact, plus quick actions to email, SMS, or view notifications.",
        ],
      },
    ],
    tips: [
      "Focus on the top lead-score band first — they're the most likely to convert.",
      "Hand long-tail nurture conversations to the AI so you can spend your time on hot leads.",
    ],
  },
  {
    id: "property-searches",
    title: "Property Searches",
    lead:
      "Every property your contacts have valued through your tools, ready to action.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Each row shows the property address, estimated price range, beds/baths/parking and the contact who searched.",
          "Status filter to focus on 'To Review' searches you haven't actioned yet.",
          "A complete checkbox per row so the list stays clean as you work through it.",
          "Copy buttons for the address and the contact's email.",
          "Quick send-email and send-SMS shortcuts to reach the contact directly.",
          "An agent badge so multi-agent teams know who owns the contact.",
          "Optional CMA file attachments for properties where one's been generated.",
        ],
      },
    ],
    tips: [
      "Filter to 'To Review' each morning to catch overnight searches from hot prospects.",
      "Mark as complete after you've sent a CMA or follow-up so you don't accidentally re-contact the same lead.",
    ],
  },
  {
    id: "notification-history",
    title: "Notification History",
    lead:
      "Timeline of every action your contacts have taken on the campaigns and assets you've sent.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "A colour-coded, icon-led timeline of opens, clicks, views, requests, unsubscribes and more.",
          "Campaign filter (Market Update, Property Report, Appraisal, etc.) and event filter (Opened, Clicked, Bounced, etc.).",
          "Contact and property details on every row so you can decide what to do next without leaving the page.",
          "Relative timestamps ('2h ago', 'yesterday') with the exact date on hover.",
          "Copy and download options for emails or filtered reports.",
          "Search by contact name, email or address.",
        ],
      },
    ],
    tips: [
      "Skim the 'Clicked' filter weekly — these are warm contacts asking for a follow-up.",
      "Run the 'Unsubscribed' filter monthly to keep your notification preferences clean and deliverability strong.",
    ],
  },
  {
    id: "activity-explorer",
    title: "Activity Explorer",
    lead:
      "Deep-dive into every platform and email event — perfect for spotting buying intent.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "30+ event types across email (delivered, opened, clicked, bounced, spam) and on-platform (property analyses, appraisals, article reads, form submits).",
          "Multi-filter view so you can combine signals (e.g. opened an email AND viewed an appraisal in the last 7 days).",
          "Tooltips explaining what each event means.",
          "Scroll-depth tracking on articles and property pages so you can tell skimmers from genuine readers.",
          "Campaign attribution showing which send drove each interaction.",
        ],
      },
    ],
    tips: [
      "Combine 'opened email' + 'viewed appraisal' to surface contacts showing real buying intent.",
      "Sort by scroll depth to find your most engaged readers — they convert at the highest rate.",
    ],
  },
  {
    id: "map-prospector",
    title: "Map Prospector",
    lead:
      "Geographic view of all the activity happening across your contact base.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "An interactive Google Map with markers for every event tied to a property or contact location.",
          "Automatic clustering as you zoom out, with drill-down on click.",
          "Distinct icons and colours per event type for quick visual reading.",
          "Filters by campaign and event type to isolate the signal you care about.",
          "Popups with contact, event and timestamp details on each marker.",
          "Suburb / address search to jump anywhere in your patch.",
        ],
      },
    ],
    tips: [
      "Spot suburbs with high marker density — those are your warmest farm areas.",
      "Filter to 'Property Appraisal' events to see where prospects are seriously considering a valuation, then door-knock or letterbox the hot streets.",
    ],
  },
  {
    id: "ai-articles",
    title: "AI Articles",
    lead:
      "Branded, AI-generated articles you can publish to your landing page, email or social channels.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "A list of every article with live/draft status and publish date.",
          "One-click publish / unpublish toggle.",
          "A rich-text editor with headings, bold, italics, links, images and lists.",
          "AI generation panel — describe the topic, choose suburbs, hit generate and you have a full article.",
          "Cover image upload (or AI generation) with preview.",
          "Suburb multi-select so the article targets the right contacts.",
          "Social blurb generator for sharing on Facebook, Instagram or email.",
          "Duplicate and delete actions to manage your library.",
          "Optional direct-to-Facebook publishing (where enabled).",
        ],
      },
    ],
    tips: [
      "Aim for 4–5 articles a month across different suburbs — each one takes minutes with the generator.",
      "Always generate the social blurb before sharing; it follows engagement best practice so you don't have to think about it.",
    ],
  },
  {
    id: "ai-video",
    title: "AI Video",
    lead:
      "Property videos generated from a listing's images, with music, captions and transitions baked in.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Listing lookup that confirms beds, baths and carspaces before you start.",
          "Image selection: let the AI auto-pick up to 8 of the best images, or curate the order manually.",
          "Music library with style filters (cinematic, upbeat, calm) and a preview player.",
          "Auto-generated headlines and subtitles for each slide based on the property details.",
          "Progress tracking while the video renders.",
          "Slide reordering, caption editing and image swapping before final export.",
          "MP4 download for email, social or your website.",
          "Library of every video you've created.",
        ],
      },
    ],
    tips: [
      "AI auto-select is excellent — start there and only adjust the slides you'd change.",
      "Match music to property type: cinematic for premium homes, upbeat for first-home buyers, calm for family homes.",
    ],
  },
  {
    id: "appraisals",
    title: "Appraisals",
    lead:
      "Professional property appraisals with comparable sales, market context and shareable outputs.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "New-appraisal flow capturing address, beds, baths, carspaces, property type, condition and features.",
          "Property image upload so the AI can incorporate context.",
          "Automatic selection of 3–5 nearby comparable sales, fully editable.",
          "Add / remove comparables and adjust the implied range.",
          "Price range and point estimate calculated from the comps.",
          "Optional rental estimate for investor-focused appraisals.",
          "Draft vs. completed status.",
          "Map view of the subject property and comparables.",
          "Public shareable link, PDF download, and direct email-to-prospect.",
          "A library of every appraisal you've created.",
        ],
      },
    ],
    tips: [
      "Sense-check the AI's comparable picks against your local knowledge before sending — swap any that don't reflect the subject property's quality.",
      "Add 2–3 sentences of local market context (e.g. 'Bondi beach suburbs up 8% YoY') to justify the range and increase credibility.",
    ],
  },
  {
    id: "qr-code",
    title: "QR Code",
    lead: "A scannable code that takes prospects to your branded landing page.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Your unique QR code, downloadable as PNG (digital) or PDF (print).",
          "Copy-link button if you'd rather share the URL than the code.",
          "Daily scan-count chart so you can tell what's working.",
          "Preview of how your profile (name, title, photo, contact details) appears to anyone who scans.",
        ],
      },
    ],
    tips: [
      "Print the code on every flyer, sign and business card — it's the lowest-effort way to drive profile traffic.",
      "Watch the scan chart weekly: a sudden spike means a campaign or flyer is performing — double down on that channel.",
    ],
  },
  {
    id: "settings",
    title: "Settings",
    lead: "Your profile, agency branding, integrations and account preferences.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Profile fields: full name, first name, email, job title, mobile, bio — these appear on every report, landing page and outbound email.",
          "A separate notification email so an assistant or team inbox can be CC'd on alerts.",
          "Agency name and location for consistent branding across every output.",
          "Email verification to unlock outbound notifications.",
          "CRM integration (Domain, PropertyMe and more) for automatic contact sync.",
          "Email-history upload (.mbox) so the AI can learn your tone from past correspondence.",
          "Contact export to CSV for backup or external use.",
          "Sign-out for shared devices.",
        ],
      },
    ],
    tips: [
      "Point the notification email at a team inbox so the whole team sees alerts and follow-up speed improves.",
      "Upload your email history — the AI will write follow-ups in your voice instead of a generic tone.",
    ],
  },
  {
    id: "billing",
    title: "Billing",
    lead: "Your plan, credit balance, top-ups and auto-reload — all in one place.",
    body: [
      {
        heading: "What you'll see",
        bullets: [
          "Current plan and next renewal date.",
          "Contact-based pricing so your subscription scales with your database size.",
          "A breakdown of Growth-plan features (unlimited notifications, branded emails, articles, appraisals, CRM sync, priority support).",
          "FAQ section covering cancellation, billing cycle and payment methods.",
          "Credit balance for on-demand services (extra video renders, SMS sends, etc.).",
          "Top-up presets ($10, $25, $50, $100, $250) and custom amounts.",
          "Auto-reload settings so credits top up automatically below a threshold.",
          "Secure Stripe payments (Visa, Mastercard, Amex).",
          "Direct link to the Stripe portal for invoices and payment method updates.",
        ],
      },
    ],
    tips: [
      "Turn on auto-reload at a $25 threshold so you never run out of credits mid-campaign.",
      "Most agents recoup the Growth plan in saved content-creation time within the first month.",
    ],
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export default function Docs() {
  const location = useLocation();
  const isMobile = useIsMobile();

  // Scroll to the hash target after render
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash]);

  useEffect(() => {
    document.title = "Docs — Vendorleads";
  }, []);

  return (
    <>
      <Navbar />
      <div
        style={{
          background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 200px)",
          minHeight: "100vh",
          color: TEXT,
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Hero */}
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: isMobile ? "2.5rem 1.25rem 1.5rem" : "4rem 2rem 2rem",
          }}
        >
          <p
            style={{
              color: ACCENT,
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              margin: 0,
            }}
          >
            Product Docs
          </p>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "2.75rem",
              lineHeight: 1.15,
              margin: "0.5rem 0 0.75rem",
              color: BRAND,
              fontWeight: 800,
            }}
          >
            How Vendorleads works
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.125rem",
              color: MUTED,
              maxWidth: 720,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            A tab-by-tab guide to everything in the Vendorleads platform — what
            you'll see, what you can do, and how to get the most out of it.
          </p>
        </div>

        {/* Layout: sticky TOC + content */}
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: isMobile ? "0 1.25rem 4rem" : "0 2rem 5rem",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "240px 1fr",
            gap: isMobile ? "1.5rem" : "3rem",
            alignItems: "start",
          }}
        >
          {/* TOC */}
          <nav
            aria-label="Docs table of contents"
            style={
              isMobile
                ? {
                    background: "#fff",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: "1rem 1.25rem",
                  }
                : {
                    position: "sticky",
                    top: 24,
                    alignSelf: "start",
                    maxHeight: "calc(100vh - 48px)",
                    overflowY: "auto",
                    paddingRight: "0.5rem",
                  }
            }
          >
            <p
              style={{
                fontSize: "0.7rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: MUTED,
                margin: "0 0 0.75rem",
              }}
            >
              On this page
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {SECTIONS.map((s) => (
                <li key={s.id} style={{ marginBottom: 6 }}>
                  <a
                    href={`#${s.id}`}
                    style={{
                      color: TEXT,
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      lineHeight: 1.4,
                      display: "block",
                      padding: "4px 0",
                      borderLeft: "2px solid transparent",
                      paddingLeft: 10,
                      marginLeft: -10,
                      transition: "color 0.15s, border-color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = ACCENT;
                      e.currentTarget.style.borderLeftColor = ACCENT;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = TEXT;
                      e.currentTarget.style.borderLeftColor = "transparent";
                    }}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content */}
          <main style={{ minWidth: 0 }}>
            {SECTIONS.map((s, idx) => (
              <section
                key={s.id}
                id={s.id}
                style={{
                  scrollMarginTop: 24,
                  paddingTop: idx === 0 ? 0 : "2.5rem",
                  paddingBottom: "2rem",
                  borderBottom:
                    idx === SECTIONS.length - 1 ? "none" : `1px solid ${BORDER}`,
                }}
              >
                <h2
                  style={{
                    fontSize: isMobile ? "1.5rem" : "1.75rem",
                    margin: "0 0 0.5rem",
                    color: BRAND,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {s.title}
                </h2>
                <p
                  style={{
                    fontSize: "1rem",
                    color: MUTED,
                    margin: "0 0 1.5rem",
                    lineHeight: 1.6,
                  }}
                >
                  {s.lead}
                </p>

                {s.body.map((block, i) => (
                  <div key={i} style={{ marginBottom: "1.5rem" }}>
                    <h3
                      style={{
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: TEXT,
                        margin: "0 0 0.5rem",
                      }}
                    >
                      {block.heading}
                    </h3>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "1.25rem",
                        color: TEXT,
                        lineHeight: 1.65,
                        fontSize: "0.95rem",
                      }}
                    >
                      {block.bullets.map((b, j) => (
                        <li key={j} style={{ marginBottom: 6 }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {s.tips && s.tips.length > 0 && (
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(59,130,246,0.06))",
                      border: "1px solid rgba(16,185,129,0.25)",
                      borderRadius: 12,
                      padding: "1rem 1.25rem",
                      marginTop: "1rem",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: ACCENT,
                        margin: "0 0 0.5rem",
                      }}
                    >
                      Pro tips
                    </p>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "1.25rem",
                        color: TEXT,
                        lineHeight: 1.6,
                        fontSize: "0.9rem",
                      }}
                    >
                      {s.tips.map((t, k) => (
                        <li key={k} style={{ marginBottom: 4 }}>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            ))}
          </main>
        </div>
      </div>
    </>
  );
}
